import { FormEvent, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import Email from './Email';
import OTP from './OTP';
import { getOTP, matchOTP } from '@/services/OTP';
import { useNavigate } from '@tanstack/react-router';
import { EClaimType } from '../../lib/types';
import { IconButton } from '../ui/IconButton';
import { Check, Send } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { LanguagePicker } from '../ui/language-picker';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { envs } from '@/lib/constants';

interface IRightSide {
  refundId?: string;
}

const RightSide = ({ refundId }: IRightSide) => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const onToggleOTP = () => {
    setIsOtpSent(!isOtpSent);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setInput(e.target.value);

  const sendOtpMutation = useMutation({
    mutationFn: getOTP,

    onSuccess: () => {
      onToggleOTP();
    },
  });

  const validateOtpMutation = useMutation({
    mutationFn: matchOTP,

    onSuccess: (data) => {
      setOtp('');
      sessionStorage.setItem('token', data.token);
      navigate({
        to: '/payments/payment-methods',
        search: {
          claimType: EClaimType.EMAIL,
          step: 1,
        },
      });
    },
  });

  const getRecaptchaToken = async () => {
    return (await recaptchaRef.current?.executeAsync()) ?? '';
  };

  const validateOtp = async () => {
    const reCaptchaToken = await getRecaptchaToken();
    validateOtpMutation.mutate({
      refundId,
      otp,
      reCaptchaToken,
    });
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isOtpSent) {
      validateOtp();
      return;
    }

    const reCaptchaToken = await getRecaptchaToken();
    sendOtpMutation.mutate({ refundId, email: input, reCaptchaToken });
  };

  const resendOTP = async () => {
    const reCaptchaToken = await getRecaptchaToken();
    await getOTP({ refundId, email: input, reCaptchaToken });
  };

  useEffect(() => {
    if (otp.length === 6) {
      validateOtp();
    }
  }, [otp]);

  return (
    <div className='relative flex h-full flex-col items-center justify-center bg-secondary text-center'>
      <div className='absolute top-4 right-4 z-50'>
        <LanguagePicker />
      </div>
      <div className='max-w-[350px]'>
        <div className='mb-8 flex flex-col gap-2'>
          <h3 className='text-2xl font-semibold'>
            {!isOtpSent ? t('Welcome') : t('Verify your email address')}
          </h3>
          <p className='text-sm text-muted-foreground'>
            {!isOtpSent
              ? t(
                  'You are just a couple of steps away from instant access to your funds.',
                )
              : t(`A one time password has been sent to`, { input })}
          </p>
        </div>
        {envs.DISABLE_RECAPTCHA ? null : (
          <ReCAPTCHA
            ref={recaptchaRef}
            size='invisible'
            sitekey={envs.RECAPTCHA_KEY}
          />
        )}
        <form
          className='mb-6 flex flex-col items-center gap-2'
          onSubmit={onSubmit}
        >
          {!isOtpSent ? (
            <Email value={input} onChange={onChange} />
          ) : (
            <OTP value={otp} setValue={setOtp} />
          )}
          <IconButton
            isLoading={
              sendOtpMutation.isPending || validateOtpMutation.isPending
            }
            variant='success'
            className='w-full'
            type='submit'
            Icon={isOtpSent ? Check : Send}
          >
            {isOtpSent ? t('Submit') : t('Send Email Authentication Code')}
          </IconButton>
          {isOtpSent && (
            <Button
              variant='outline'
              className='w-full'
              type='button'
              onClick={onToggleOTP}
            >
              {t('Cancel')}
            </Button>
          )}
        </form>

        {isOtpSent ? (
          <button className='text-primary-700 underline' onClick={resendOTP}>
            {t('Resend one time password')}
          </button>
        ) : (
          <p className='text-balance text-muted-foreground'>
            {t('By clicking continue, you agree to our')}{' '}
            <a
              href='https://www.clientpaydirect.com/privacy-policy/'
              target='_blank'
              className='underline'
            >
              {t('Terms of Service')}
            </a>{' '}
            and{' '}
            <a
              href='https://www.clientpaydirect.com/privacy-policy/'
              target='_blank'
              className='underline'
            >
              {t('Privacy Policy.')}
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default RightSide;
